// The anonymous read contract against the platform's public API — shared by both public adoption
// sites (DierMatch under /dieren, ikzoekbaas under its own host).
//
// Three unauthenticated endpoints (no bearer; these serve only `live`, privacy-whitelisted data):
//   GET /api/public/publications            → overview page { items, totalElements, soorten }
//   GET /api/public/publications/{slug}      → one animal's full detail
//   GET /api/public/media/{platformMediaId}  → a photo's bytes (used directly as an <img src>)
//
// Every read takes an optional `kanaal` (story 8-2). A site MUST pass its own channel: the same
// store backs both sites, and without it an ikzoekbaas visitor would see DierMatch-only animals.
// The detail call passes it too, so a deep link to an animal from the other site 404s instead of
// rendering.
//
// Plain fetch (no auth headers) against the same `/api` base the dataProvider uses — same-origin in the
// single-artifact deploy, dev-proxied to the platform API otherwise.

const API_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? "/api";

/** Which public site is asking. Mirrors the platform `PublicationChannel`. */
export type Kanaal = "DIERMATCH" | "IKZOEKBAAS";

/** One overview card. Mirrors the platform `PublicAnimalSummary`. */
export interface PublicAnimalSummary {
  platformId: string;
  slug: string;
  naam: string;
  soort: string | null;
  ras: string | null;
  geslacht: string | null;
  leeftijd: string | null;
  /** ISO date or null — the card prints it in full Dutch after the gender, as the site does. */
  geboortedatum: string | null;
  beschikbaarheid: string | null;
  locatie: string | null;
  /** De woonplaats van de opvang, gelezen uit haar adres. Wat de echte site op de kaart zet. */
  plaats: string | null;
  intro: string | null;
  tags: string[];
  kenmerken: string[];
  hoofdfotoUrl: string | null;
  hoofdfotoAlt: string | null;
  opvangNaam: string | null;
}

/** A page of overview cards + the soort facet. Mirrors the platform `PublicAnimalPage`. */
export interface PublicAnimalPage {
  items: PublicAnimalSummary[];
  totalElements: number;
  page: number;
  size: number;
  soorten: string[];
}

/** A gallery photo on the detail page. */
export interface PublicFoto {
  url: string;
  alt: string | null;
  hoofdfoto: boolean;
}

/** One row of the ikzoekbaas traits table: the catalogue key + the answer the shelter wrote. */
export interface PublicKenmerk {
  sleutel: string;
  /** The Dutch heading, supplied by the platform so the detail table and the facets cannot drift. */
  label: string;
  antwoord: string;
}

/**
 * The shelter's public contact card. Organisation contact data only (name, visiting address, general
 * phone/mailbox) — null when the publication carries none, in which case the site omits the block.
 */
export interface PublicContact {
  naam: string | null;
  adres: string | null;
  telefoon: string | null;
  email: string | null;
  url: string | null;
  tekst: string | null;
}

/** One animal's full detail. Mirrors the platform `PublicAnimalDetail`. */
export interface PublicAnimalDetail {
  platformId: string;
  slug: string;
  naam: string;
  soort: string | null;
  ras: string | null;
  geslacht: string | null;
  leeftijd: string | null;
  /** ISO date or null — the site decides how to word it, the API does not pick a locale. */
  geboortedatum: string | null;
  beschikbaarheid: string | null;
  locatie: string | null;
  tags: string[];
  kenmerken: string[];
  kenmerkAntwoorden: PublicKenmerk[];
  intro: string | null;
  overDitDier: string | null;
  pastBij: string | null;
  belangrijk: string | null;
  fotos: PublicFoto[];
  opvangNaam: string | null;
  contact: PublicContact | null;
  publishedAt: string | null;
}

export interface ListFilters {
  soort?: string;
  q?: string;
  tag?: string;
  kanaal?: Kanaal;
  page?: number;
  size?: number;
}

/** Absolute URL for a media handle — usable directly as an `<img src>`. */
export function mediaUrl(handle: string): string {
  return `${API_URL}/public/media/${encodeURIComponent(handle)}`;
}

async function getJson<T>(path: string): Promise<T> {
  const response = await fetch(`${API_URL}${path}`, { headers: { Accept: "application/json" } });
  if (!response.ok) {
    throw { message: response.statusText || `HTTP ${response.status}`, statusCode: response.status };
  }
  return (await response.json()) as T;
}

/** Fetch a page of the overview with optional filters. */
export async function fetchPublications(filters: ListFilters = {}): Promise<PublicAnimalPage> {
  const params = new URLSearchParams();
  if (filters.soort) params.set("soort", filters.soort);
  if (filters.q) params.set("q", filters.q);
  if (filters.tag) params.set("tag", filters.tag);
  if (filters.kanaal) params.set("kanaal", filters.kanaal);
  if (filters.page != null) params.set("page", String(filters.page));
  if (filters.size != null) params.set("size", String(filters.size));
  const query = params.toString();
  return getJson<PublicAnimalPage>(`/public/publications${query ? `?${query}` : ""}`);
}

/**
 * Fetch one animal's detail by slug (or platformId). Rejects with an HttpError-shaped object on 404 —
 * which is also what a publication outside `kanaal` returns.
 */
export async function fetchPublication(slug: string, kanaal?: Kanaal): Promise<PublicAnimalDetail> {
  const query = kanaal ? `?kanaal=${encodeURIComponent(kanaal)}` : "";
  return getJson<PublicAnimalDetail>(`/public/publications/${encodeURIComponent(slug)}${query}`);
}

/**
 * De bewaarde dieren van deze bezoeker, op slug — alleen de dieren die er nog zijn.
 *
 * <p>Wat niet terugkomt, is er niet meer: geplaatst, ingetrokken, of van een ander kanaal. De site
 * bewaart wat ze terugkrijgt, en zo valt een geplaatst dier vanzelf uit de favorieten.
 */
export async function fetchBewaard(
  slugs: readonly string[],
  kanaal?: Kanaal,
): Promise<PublicAnimalSummary[]> {
  if (slugs.length === 0) return [];
  const params = new URLSearchParams();
  params.set("slugs", slugs.join(","));
  if (kanaal) params.set("kanaal", kanaal);
  return getJson<PublicAnimalSummary[]>(`/public/bewaard?${params.toString()}`);
}

/** Narrow an unknown rejection to its HTTP status (to distinguish 404 from a network error). */
export function statusOf(error: unknown): number | undefined {
  return (error as { statusCode?: number } | undefined)?.statusCode;
}

// ── faceted search (story 8-4) ───────────────────────────────────────────────────────────────────

/** One checkbox in the filter sidebar. */
export interface FacetOptie {
  /** The value to send back as a filter — stable and URL-safe. */
  waarde: string;
  /** What the checkbox reads. Differs from `waarde` for age, whose value is a bucket code. */
  label: string;
  aantal: number;
  gekozen: boolean;
}

/** One filter block — a question and its possible answers, each with a live count. */
export interface Facet {
  sleutel: string;
  label: string;
  opties: FacetOptie[];
}

/** One category and how many live animals it holds — the home page tiles. */
export interface GroepTelling {
  pad: string;
  label: string;
  aantal: number;
}

export interface PublicSearchResult {
  items: PublicAnimalSummary[];
  totalElements: number;
  page: number;
  size: number;
  soortGroep: string | null;
  groepen: GroepTelling[];
  facetten: Facet[];
  sortering: string;
}

/** How the results are ordered. Mirrors the platform `PublicSearchQuery.Sortering`. */
export type Sortering = "nieuw" | "langstzittend";

/**
 * Everything the visitor asked of the search page. `kenmerken` is keyed by catalogue key with the
 * selected answers as values — OR within a key, AND across keys, which is what the sidebar's
 * multi-select checkboxes mean.
 */
export interface SearchFilters {
  kanaal?: Kanaal;
  groep?: string;
  q?: string;
  alleenBeschikbaar?: boolean;
  /** Only animals whose shelter answered the special-care question with something other than no. */
  alleenBuitenbeentjes?: boolean;
  geslacht?: string[];
  leeftijd?: string[];
  ras?: string[];
  opvang?: string[];
  kenmerken?: Record<string, string[]>;
  /** Story 8-8 — alleen de dieren van één opvang, op haar sleutel. */
  opvangId?: string;
  /** Waar de bezoeker vandaan komt: een postcode ("9723 CN") of een plaatsnaam ("Groningen"). */
  postcode?: string;
  /** Hoe ver hij wil rijden, in kilometers. Zonder postcode doet dit niets, en andersom ook niet. */
  straal?: number;
  sortering?: Sortering;
  page?: number;
  size?: number;
}

/** Build the query string for a search — also what the address bar shows, so a search is shareable. */
export function searchParams(filters: SearchFilters): URLSearchParams {
  const params = new URLSearchParams();
  if (filters.kanaal) params.set("kanaal", filters.kanaal);
  if (filters.groep) params.set("groep", filters.groep);
  if (filters.q) params.set("q", filters.q);
  if (filters.alleenBeschikbaar) params.set("beschikbaar", "true");
  if (filters.alleenBuitenbeentjes) params.set("buitenbeentje", "true");
  for (const waarde of filters.geslacht ?? []) params.append("geslacht", waarde);
  for (const waarde of filters.leeftijd ?? []) params.append("leeftijd", waarde);
  for (const waarde of filters.ras ?? []) params.append("ras", waarde);
  for (const waarde of filters.opvang ?? []) params.append("opvang", waarde);
  for (const [sleutel, antwoorden] of Object.entries(filters.kenmerken ?? {})) {
    for (const antwoord of antwoorden) params.append("k", `${sleutel}:${antwoord}`);
  }
  if (filters.opvangId) params.set("opvangId", filters.opvangId);
  // Alleen samen: een postcode zonder straal is nog geen filter, en een straal zonder postcode kan
  // nergens vandaan meten. De API kijkt er ook zo naar, maar dit scheelt een zinloos verzoek.
  if (filters.postcode && filters.straal) {
    params.set("postcode", filters.postcode);
    params.set("straal", String(filters.straal));
  }
  if (filters.sortering) params.set("sorteer", filters.sortering);
  if (filters.page != null) params.set("page", String(filters.page));
  if (filters.size != null) params.set("size", String(filters.size));
  return params;
}

/**
 * Story 8-8 — de opvangen die op dit kanaal iets te bieden hebben.
 *
 * <p>De sleutel is het enige veld dat niet mag veranderen: daar hangt de eigen pagina van een opvang
 * aan, en die link staat straks op de website van dat asiel.
 */
export interface PublicOpvang {
  sleutel: string;
  naam: string | null;
  plaats: string | null;
  aantal: number;
}

/**
 * Meld één weergave van deze advertentie, op de site waar gekeken wordt.
 *
 * <p>De site gaat mee omdat de backoffice per site kijkt: DierMatch en ikzoekbaas zijn twee losse
 * overzichten met elk hun eigen publiek, en dan hoort daar het cijfer van díé site te staan. Het
 * totaal loopt hoe dan ook door; de site is een gegeven over de pagina, niet over de bezoeker.
 *
 * <p>Er komt geen antwoord terug dat ergens toe doet, en een mislukking is geen probleem: een
 * tellertje mag nooit de reden zijn dat een pagina iets laat merken. Vandaar het lege `catch`.
 */
export async function meldWeergave(slug: string, site?: "DIERMATCH" | "IKZOEKBAAS"): Promise<void> {
  try {
    const waar = site ? `?site=${site}` : "";
    await fetch(`/api/public/publications/${encodeURIComponent(slug)}/weergave${waar}`,
      { method: "POST" });
  } catch {
    // Bewust stil.
  }
}

/**
 * Meld dat een bezoeker dit dier bewaard heeft, of weer losgelaten.
 *
 * <p>Alleen het getal gaat heen en weer; wie het was blijft in deze browser. Het asiel ziet
 * straks hoeveel mensen zijn dier bewaard hebben, niet wie.
 *
 * <p>Stil bij een fout, net als `meldWeergave`: een hartje dat werkt hoort niet alsnog een
 * foutmelding op te leveren omdat een tellertje de server niet haalde. De bezoeker zijn lijstje
 * staat lokaal en klopt sowieso; de teller van het asiel loopt dan één mis.
 */
export async function meldFavoriet(slug: string, bewaard: boolean): Promise<void> {
  try {
    await fetch(`/api/public/publications/${encodeURIComponent(slug)}/favoriet`, {
      method: bewaard ? "POST" : "DELETE",
    });
  } catch {
    // Bewust stil.
  }
}

/**
 * Het merk van één opvang op haar eigen DierMatch-pagina: haar logo.
 *
 * DierMatch houdt zijn eigen kleuren — een bezoeker hoort te zien dat hij op DierMatch is. Het asiel
 * is herkenbaar aan zijn logo, zijn naam en zijn tekst.
 */
export interface PublicHuisstijl {
  logoUrl?: string;
}

export async function fetchOpvangHuisstijl(sleutel: string): Promise<PublicHuisstijl | null> {
  const res = await fetch(`${API_URL}/public/opvangen/${encodeURIComponent(sleutel)}/huisstijl`);
  if (res.status === 204 || !res.ok) {
    return null;
  }
  return (await res.json()) as PublicHuisstijl;
}

/**
 * Eén opvang, ook als ze nog geen dieren heeft: een gekoppeld asiel dat zijn logo al neerzette, krijgt dan zijn
 * pagina met nul dieren in plaats van "Deze opvang kennen we niet". {@code null} als de opvang echt onbekend is.
 */
export async function fetchOpvang(sleutel: string, kanaal?: Kanaal): Promise<PublicOpvang | null> {
  const query = kanaal ? `?kanaal=${encodeURIComponent(kanaal)}` : "";
  const res = await fetch(`/api/public/opvangen/${encodeURIComponent(sleutel)}${query}`, { headers: { Accept: "application/json" } });
  if (res.status === 404) {
    return null;
  }
  if (!res.ok) {
    throw new Error(`opvang ${res.status}`);
  }
  return (await res.json()) as PublicOpvang;
}

export async function fetchOpvangen(kanaal?: Kanaal): Promise<PublicOpvang[]> {
  const query = kanaal ? `?kanaal=${encodeURIComponent(kanaal)}` : "";
  const res = await fetch(`/api/public/opvangen${query}`, { headers: { Accept: "application/json" } });
  if (!res.ok) {
    throw new Error(`opvangen ${res.status}`);
  }
  return (await res.json()) as PublicOpvang[];
}

/** Run one faceted search. */
export async function fetchSearch(filters: SearchFilters): Promise<PublicSearchResult> {
  const query = searchParams(filters).toString();
  return getJson<PublicSearchResult>(`/public/search${query ? `?${query}` : ""}`);
}

// ── success stories (epic-8) ─────────────────────────────────────────────────────────────────────

/**
 * One success story: an animal that found a home, and what the shelter wrote about it.
 *
 * <p>Only the animal — there is no field for the adopter here because the platform stores none.
 */
export interface PublicSuccesverhaal {
  platformId: string;
  naam: string;
  soort: string | null;
  verhaal: string;
  fotoUrl: string | null;
  fotoAlt: string | null;
  opvangNaam: string | null;
  geplaatstOp: string | null;
}

/** The success stories for a site, newest placement first. Empty when there are none. */
export async function fetchSuccesverhalen(
  kanaal?: Kanaal,
  limiet = 3,
): Promise<PublicSuccesverhaal[]> {
  const params = new URLSearchParams();
  if (kanaal) params.set("kanaal", kanaal);
  params.set("limiet", String(limiet));
  return getJson<PublicSuccesverhaal[]>(`/public/succesverhalen?${params.toString()}`);
}

// ── AI-matchwizard (epic 12) ─────────────────────────────────────────────────────────────────────
// Eén POST, en er wordt niets bewaard: het verzoek draagt de antwoorden, het antwoord draagt de
// uitslag, en daarna is er niets meer. Vandaar POST en geen GET — als querystring zouden de
// antwoorden over je huishouden in logboeken en in de geschiedenis van de browser belanden.

/** Eén dier in de matchuitslag. Spiegelt de platform-`PublicMatchAnimal`. */
export interface PublicMatchAnimal {
  platformId: string;
  slug: string;
  rang: number;
  /** `sterk` | `mogelijk` | `meer_informatie` — geen percentage; zie epic 12, besluit A2. */
  band: string;
  bandLabel: string;
  /** 0–1: hoeveel van het gewicht op een ingevuld antwoord berekend kon worden. */
  dekking: number;
  naam: string;
  soort: string | null;
  ras: string | null;
  geslacht: string | null;
  leeftijd: string | null;
  geboortedatum: string | null;
  beschikbaarheid: string | null;
  plaats: string | null;
  opvangNaam: string | null;
  hoofdfotoUrl: string | null;
  profielUrl: string | null;
  redenen: string[];
  aandachtspunten: string[];
  onbekend: string[];
}

export interface PublicMatchResult {
  /** Hoeveel dieren de harde filters overleefden; kan groter zijn dan de getoonde lijst. */
  totaal: number;
  /** Per harde eis hoeveel dieren erop afvielen — de herstelroute bij nul resultaten. */
  afgevallen: Record<string, number>;
  resultaten: PublicMatchAnimal[];
}

/** De versie van de vragenset; reist mee zodat een server een oud antwoord kan herkennen. */
export const MATCH_VERSIE = "2026-10-26";

export async function fetchMatch(
  antwoorden: Record<string, string[]>,
): Promise<PublicMatchResult> {
  const response = await fetch(`${API_URL}/public/match/rank`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ versie: MATCH_VERSIE, antwoorden }),
  });
  if (!response.ok) {
    throw { message: response.statusText || `HTTP ${response.status}`, statusCode: response.status };
  }
  return (await response.json()) as PublicMatchResult;
}

/** Hoe een harde eis heet in de herstelroute ("31 dieren vielen af op: kan bij katten"). */
export const EIS_LABELS: Record<string, string> = {
  soort: "de gekozen diersoort",
  beschikbaarheid: "beschikbaarheid",
  kinderen: "kan bij kinderen",
  honden: "kan bij honden",
  katten: "kan bij katten",
  "geen-extra-zorg": "geen extra zorg nodig",
  "niet-groot": "niet te groot",
  afstand: "de reisafstand",
};

/** Eén voorstel uit de vrije wens: een wizardvraag met een van haar toegestane antwoorden. */
export interface MatchChip {
  sleutel: string;
  waarde: string;
}

/**
 * Zet een vrije wens om in voorkeuren om te bevestigen (epic 12, story 12-8).
 *
 * <p>`null` betekent "deze functie bestaat hier niet" (503 — geen AI-sleutel geconfigureerd); de
 * wizard verbergt het veld dan. Een lege lijst betekent "er zat niets bruikbaars in", en dat is een
 * normaal antwoord waar de bezoeker gewoon op door kan.
 */
export async function fetchMatchChips(tekst: string): Promise<MatchChip[] | null> {
  const response = await fetch(`${API_URL}/public/match/interpret`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ tekst }),
  });
  if (response.status === 503) {
    return null;
  }
  if (!response.ok) {
    throw { message: response.statusText || `HTTP ${response.status}`, statusCode: response.status };
  }
  return ((await response.json()) as { chips: MatchChip[] }).chips ?? [];
}

/** Eén vraag uit de kennismakingslijst van een asiel. */
export interface KennismakingVraag {
  id: string;
  section?: string | null;
  sectionStart?: boolean;
  /** line | text | single | yesno | multi | number */
  type: string;
  q: string;
  options?: string[];
  helper?: string | null;
  required?: boolean;
  format?: string | null;
}

export interface KennismakingLijst {
  naam: string | null;
  vragen: KennismakingVraag[];
  dierNaam: string | null;
}

/**
 * De vragen die dit asiel stelt bij dit dier.
 *
 * `null` betekent "dit asiel heeft voor deze diersoort geen lijst" (404), en dat is een normaal
 * antwoord: het scherm valt dan terug op het contactblok dat er altijd al was. Een asiel zonder
 * vragenlijst hoort niet onbereikbaar te worden.
 */
export async function fetchKennismaking(slug: string): Promise<KennismakingLijst | null> {
  const response = await fetch(
    `${API_URL}/public/publications/${encodeURIComponent(slug)}/kennismaking`,
    { headers: { Accept: "application/json" } },
  );
  if (response.status === 404) {
    return null;
  }
  if (!response.ok) {
    throw { message: response.statusText || `HTTP ${response.status}`, statusCode: response.status };
  }
  const lijst = (await response.json()) as KennismakingLijst;
  return { ...lijst, vragen: Array.isArray(lijst.vragen) ? lijst.vragen : [] };
}

export interface KennismakingAntwoord {
  veldSleutel: string;
  sectie?: string | null;
  vraag: string;
  antwoord: string;
}

export interface KennismakingInzending {
  naam: string;
  email: string;
  telefoon: string;
  plaats?: string;
  antwoorden: KennismakingAntwoord[];
}

/**
 * Verstuurt het ingevulde formulier.
 *
 * Anders dan bij een hartje wordt een fout hier wél doorgegeven: iemand die tien minuten aan een
 * formulier heeft gewerkt hoort te weten dat het niet verstuurd is.
 */
export async function verstuurKennismaking(
  slug: string,
  inzending: KennismakingInzending,
): Promise<void> {
  const response = await fetch(
    `${API_URL}/public/publications/${encodeURIComponent(slug)}/kennismaking`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(inzending),
    },
  );
  if (!response.ok) {
    throw { message: response.statusText || `HTTP ${response.status}`, statusCode: response.status };
  }
}
